// @core/stores/notifications/NotificationsChannelStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { notificationsApi } from '@core/api';
import type {
  CreateChannelRequest,
  GetChannelHealthResponse,
  NotificationChannel,
  TestChannelRequest,
  TestChannelResponse,
  UpdateChannelConfigRequest,
  UpdateChannelRequest,
  ValidateChannelConfigRequest,
  ValidateChannelConfigResponse,
} from '@core/types/notifications';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class NotificationsChannelStore {
  // List state
  items: NotificationChannel[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: NotificationChannel | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, NotificationChannel>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): NotificationChannel | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: NotificationChannel | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create notification channel
   */
  async CreateChannel(data: CreateChannelRequest): Promise<NotificationChannel> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await notificationsApi.CreateChannel(data);
      const item = response.channel;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete notification channel
   */
  async DeleteChannel(channel_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await notificationsApi.DeleteChannel(channel_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== channel_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === channel_id) {
          this.currentItem = null;
        }

        this.removeFromCache(channel_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get channel by ID
   */
  async GetChannel(channel_id: string): Promise<NotificationChannel> {
    // Check cache first
    const cached = this.cache.get(channel_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await notificationsApi.GetChannel(channel_id);
      const item = response.channel;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get channel health status
   */
  async GetChannelHealth(channel_id: string): Promise<GetChannelHealthResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.GetChannelHealth(channel_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List notification channels
   */
  async ListChannels(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await notificationsApi.ListChannels(params);

      runInAction(() => {
        this.items = response.channels?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Test notification channel
   */
  async TestChannel(channel_id: string, data: TestChannelRequest): Promise<TestChannelResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.TestChannel(channel_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update notification channel
   */
  async UpdateChannel(channel_id: string, data: UpdateChannelRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.UpdateChannel(channel_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update channel configuration
   */
  async UpdateChannelConfig(channel_id: string, data: UpdateChannelConfigRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.UpdateChannelConfig(channel_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Validate channel configuration
   */
  async ValidateChannelConfig(data: ValidateChannelConfigRequest): Promise<ValidateChannelConfigResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await notificationsApi.ValidateChannelConfig(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const notificationsChannelStore = new NotificationsChannelStore();
